import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, PROVIDER_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Sparkles, KeyRound, Check, Info } from "lucide-react";

const FIELDS = [
  { key: "anthropic_key", provider: "anthropic", label: "Anthropic (Claude) API Key", placeholder: "sk-ant-..." },
  { key: "openai_key", provider: "openai", label: "OpenAI API Key", placeholder: "sk-..." },
  { key: "gemini_key", provider: "gemini", label: "Google Gemini API Key", placeholder: "AIza..." },
];

export default function Settings() {
  const [status, setStatus] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setStatus(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {};
    Object.entries(values).forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
    if (Object.keys(payload).length === 0) return toast.error("Ən azı bir açar daxil edin");
    setSaving(true);
    try {
      const { data } = await api.put("/settings", payload);
      setStatus(data);
      setValues({});
      toast.success("Açarlar yadda saxlanıldı");
    } catch (e) {
      toast.error("Saxlanılmadı");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-50 border-b border-[#222] bg-black/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-cyan-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter">Ayarlar</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-500 mb-2">API Açarları</p>
        <h1 className="font-heading font-black text-3xl tracking-tighter mb-3">Öz Açarlarını Qoş</h1>
        <div className="flex items-start gap-2 border border-[#222] bg-[#0a0a0a] p-4 rounded-sm mb-8">
          <Info className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-400">
            Layihələrdə <span className="text-white">Universal Açar</span> ilə dərhal işləyə bilərsən (açar lazım deyil).
            Öz hesabınla işləmək istəsən, provayder açarını buraya əlavə et və layihə yaradarkən "Öz API Açarım" rejimini seç.
          </p>
        </div>

        <div className="space-y-6">
          {FIELDS.map((f) => {
            const meta = PROVIDER_META[f.provider];
            const has = status[`has_${f.provider}`];
            return (
              <div key={f.key} className="border border-[#222] p-5 rounded-sm bg-[#0a0a0a]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                    <Label className="text-sm font-medium">{f.label}</Label>
                  </div>
                  {has && (
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-emerald-500" data-testid={`status-${f.provider}`}>
                      <Check className="w-3 h-3" /> Qoşulub
                    </span>
                  )}
                </div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    type="password"
                    value={values[f.key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    placeholder={has ? "•••••••••• (dəyişmək üçün yenidən daxil et)" : f.placeholder}
                    className="rounded-sm bg-transparent border-[#333] focus:ring-1 focus:ring-cyan-500 pl-10 font-mono text-sm"
                    data-testid={`input-${f.provider}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Button
          onClick={save} disabled={saving}
          className="mt-8 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-medium"
          data-testid="save-settings"
        >
          {saving ? "Saxlanılır..." : "Açarları Saxla"}
        </Button>
      </main>
    </div>
  );
}
